/**
 * Ant Design + axios adapters over the framework-agnostic translator in
 * `lib/validationError`. The guide's snippet talks to a generic
 * `form.setError(path, msg)` / `form.setFormError(msg)` API; this repo uses
 * antd's `Form` instance (`form.setFields`) and axios, so the glue lives here.
 *
 * See apps/invoicing/frontend-snippets/README.md for the backend contract.
 */
import type { FormInstance } from "antd";
import {
    isValidationError,
    mapAckToPayload,
    mapValidationToPayload,
    type MappedValidation,
    type ValidationErrorBody,
    type WsAck,
} from "lib/validationError";
import { duplicateKeyFromAxios } from "./duplicateKeyError";
import { failedNotification } from "./notifications";
import { handleApiError } from "./handleApiError";

/**
 * Pull a `validation_error` body out of an axios error, but only when the
 * server actually returned `400 + type:"validation_error"`. Any other failure
 * (401/404/409/500, network error, …) returns `null` so the caller falls back
 * to its existing generic handling (`handleApiError`).
 */
export function restValidationError(error: any): ValidationErrorBody | null {
    const status = error?.response?.status;
    const body = error?.response?.data;
    return status === 400 && isValidationError(body) ? body : null;
}

/**
 * Attach each issue to its antd form control by `path` and return the messages
 * that have no single field to live on (cross-field `@Rule` violations, empty
 * `path`) so the caller can surface them at form/toast level.
 *
 * `form.setFields` keys errors by the field `name`. The backend `path`
 * ("fixedQuantity", "items.0.name") is split into antd's array-name form via
 * the issue's `segments`, so nested/array fields resolve too.
 */
export function applyValidationToForm(form: FormInstance, mapped: MappedValidation): string[] {
    const fields = Object.entries(mapped.byPath)
        .filter(([path]) => path !== "")
        .map(([, issues]) => ({
            name: issues[0].segments.length > 1 ? issues[0].segments : issues[0].path,
            errors: issues.map((i) => i.message),
        }));

    if (fields.length) {
        form.setFields(fields as any);
    }
    return mapped.formLevel.map((i) => i.message);
}

/**
 * REST convenience: if `error` is a validation error, map it onto `form`
 * against the `payload` that was submitted and return the form-level messages
 * (empty array when fully field-mapped). Returns `null` when it is NOT a
 * validation error — the caller should then run its normal error handling.
 */
export function applyRestValidationError(
    error: any,
    payload: unknown,
    form: FormInstance
): string[] | null {
    const body = restValidationError(error);
    if (!body) return null;
    return applyValidationToForm(form, mapValidationToPayload(body, payload));
}

/**
 * WebSocket convenience: mirror of {@link applyRestValidationError} for a
 * `WsAck`. Returns the form-level messages when the ack is a validation
 * failure, otherwise `null` (success OR generic exception — branch on
 * `ack.ok` afterwards).
 */
export function applyAckValidationError<T>(
    ack: WsAck<T>,
    payload: unknown,
    form: FormInstance
): string[] | null {
    const mapped = mapAckToPayload(ack, payload);
    if (!mapped) return null;
    return applyValidationToForm(form, mapped);
}

/**
 * REST convenience: if `error` is a Mongo duplicate-key (E11000) failure,
 * surface it — inline on the matching control when we can name the field,
 * otherwise as a toast — and return `true` (handled). Returns `false` when it
 * is NOT a duplicate-key error, so the caller falls through to its normal
 * handling. `opts.field`/`opts.message` override the auto-detected field/text
 * (e.g. when the index name doesn't match the form field).
 */
export function applyRestDuplicateKeyError(
    error: any,
    form: FormInstance,
    opts: { field?: string; message?: string; noun?: string } = {}
): boolean {
    const dup = duplicateKeyFromAxios(error, opts.noun);
    if (!dup) return false;
    const field = opts.field ?? dup.field;
    const message = opts.message ?? dup.message;
    if (field) {
        form.setFields([{ name: field, errors: [message] }]);
    } else {
        failedNotification("Error", message);
    }
    return true;
}

/**
 * tsoa convenience (the Google Projects API): request-validation failures come
 * back as `422 { name: "ValidateError", details: { "body.field": { message } } }`.
 * Map those onto the antd form when the key names a form field, and return the
 * messages that could not be mapped (empty array when all mapped). Returns
 * `null` when the error is not a tsoa validation error so the caller falls
 * through to its generic handling.
 */
export function applyTsoaValidationError(
    error: any,
    form: FormInstance,
    fieldNames: string[]
): string[] | null {
    const status = error?.response?.status;
    const body = error?.response?.data;
    if (status !== 422 || !body?.details || typeof body.details !== "object") {
        return null;
    }

    const unmapped: string[] = [];
    const fields: { name: string; errors: string[] }[] = [];

    Object.entries<any>(body.details).forEach(([key, issue]) => {
        const message: string = issue?.message || `${key} is invalid`;
        const segments = key.split(".");
        const field = segments[segments.length - 1];
        if (fieldNames.includes(field)) {
            fields.push({ name: field, errors: [message] });
        } else {
            unmapped.push(`${key}: ${message}`);
        }
    });

    if (fields.length) form.setFields(fields as any);
    return unmapped;
}

/**
 * Everything a form submit can get back, in order: a tsoa 422, a REST
 * `validation_error`, a duplicate key — each mapped inline onto `form` when it
 * can be — and otherwise the generic toast. Returns nothing; the caller just
 * stops (the form stays open with the errors shown).
 */
export function applyFormSubmitError(
    error: any,
    form: FormInstance,
    opts: { payload: unknown; fieldNames: string[]; fallback: string; noun?: string }
): void {
    const tsoa = applyTsoaValidationError(error, form, opts.fieldNames);
    if (tsoa !== null) {
        if (tsoa.length) failedNotification("Error", tsoa.join(" "));
        return;
    }
    const rest = applyRestValidationError(error, opts.payload, form);
    if (rest !== null) {
        if (rest.length) failedNotification("Error", rest.join(" "));
        return;
    }
    if (applyRestDuplicateKeyError(error, form, { noun: opts.noun })) return;
    handleApiError(error, opts.fallback);
}
